import apiClient from "./apiClient";
import type { IInventory } from "./inventoryApi";

const MONGO_ID_RE = /^[a-f\d]{24}$/i;

export interface InventoryPool {
  _id: string;
  name: string;
  isDefault?: boolean;
}

export interface PoolTransferItem {
  inventoryItemId: string;
  quantity: number;
}

export interface InventoryQuantityUpdate {
  inventoryItemId: string;
  originalQuantity: number;
  newQuantity: number;
}

export interface InventoryQuantityConflict {
  inventoryItemId: string;
  originalQuantity: number;
  currentQuantity: number;
}

const normalizePool = (raw: any): InventoryPool | null => {
  const id = String(raw?._id ?? raw?.id ?? "").trim();
  const name = String(raw?.name ?? "").trim();
  if (!id || !name) return null;
  const isDefault = Boolean(raw?.isDefault);
  if (!isDefault && !MONGO_ID_RE.test(id)) return null;
  return { _id: id, name, isDefault };
};

const parsePoolsPayload = (payload: any): InventoryPool[] => {
  const rawList = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.pools)
    ? payload.pools
    : Array.isArray(payload?.items)
    ? payload.items
    : [];

  const pools = rawList
    .map(normalizePool)
    .filter((pool: InventoryPool | null): pool is InventoryPool =>
      Boolean(pool)
    );

  return pools.length ? pools : [{ _id: "main", name: "Main", isDefault: true }];
};

const poolEndpoints = ["/inventory/pools", "/inventoryPools"];

export const fetchInventoryPools = async (): Promise<InventoryPool[]> => {
  for (const path of poolEndpoints) {
    try {
      const response = await apiClient.get(path);
      return parsePoolsPayload(response.data);
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        throw new Error(
          error?.response?.data?.message || "Failed to load inventory pools"
        );
      }
    }
  }
  return [{ _id: "main", name: "Main", isDefault: true }];
};

export const createInventoryPool = async (name: string): Promise<InventoryPool[]> => {
  const trimmed = name.trim();
  if (!trimmed) throw new Error("Pool name is required");

  for (const path of poolEndpoints) {
    try {
      await apiClient.post(path, { name: trimmed });
      return fetchInventoryPools();
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        throw new Error(
          error?.response?.data?.message || "Failed to create inventory pool"
        );
      }
    }
  }

  throw new Error("Pool create endpoint is not available on this backend.");
};

export const renameInventoryPool = async (
  poolId: string,
  name: string
): Promise<InventoryPool[]> => {
  const trimmedId = poolId.trim();
  const trimmedName = name.trim();
  if (!trimmedId) throw new Error("Pool id is required");
  if (!trimmedName) throw new Error("Pool name is required");

  for (const basePath of poolEndpoints) {
    try {
      await apiClient.put(`${basePath}/${encodeURIComponent(trimmedId)}`, {
        name: trimmedName,
      });
      return fetchInventoryPools();
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        throw new Error(
          error?.response?.data?.message || "Failed to rename inventory pool"
        );
      }
    }
  }

  throw new Error("Pool rename endpoint is not available on this backend.");
};

export const transferInventoryBetweenPools = async (params: {
  fromPoolId: string;
  toPoolId: string;
  items: PoolTransferItem[];
}) => {
  const items = (params.items || []).filter(
    (item) => item.inventoryItemId && Number(item.quantity) > 0
  );

  if (!params.fromPoolId || !params.toPoolId) {
    throw new Error("Both source and destination pools are required.");
  }
  if (params.fromPoolId === params.toPoolId) {
    throw new Error("Source and destination pools must be different.");
  }
  if (!items.length) {
    throw new Error("Pick at least one item to transfer.");
  }

  for (const path of ["/inventory/pools/transfer", "/inventoryPools/transfer"]) {
    try {
      const response = await apiClient.post(path, {
        fromPoolId: params.fromPoolId,
        toPoolId: params.toPoolId,
        items,
      });
      return response.data;
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        throw new Error(
          error?.response?.data?.message || "Failed to transfer inventory"
        );
      }
    }
  }

  throw new Error("Pool transfer endpoint is not available on this backend.");
};

export const getInventoryByPool = async (poolId: string): Promise<IInventory[]> => {
  const response = await apiClient.get("/inventory", {
    params: { poolId },
  });
  const payload = response.data;
  return Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload)
    ? payload
    : [];
};

export const bulkAdjustInventoryQuantities = async (params: {
  poolId: string;
  updates: InventoryQuantityUpdate[];
}) => {
  try {
    const response = await apiClient.patch("/inventory/bulk-quantity", params);
    return response.data;
  } catch (error: any) {
    const wrapped = new Error(
      error?.response?.data?.message || "Failed to save quantity changes."
    ) as Error & {
      status?: number;
      code?: string;
      conflicts?: InventoryQuantityConflict[];
    };
    wrapped.status = error?.response?.status;
    wrapped.code = error?.response?.data?.code;
    wrapped.conflicts = error?.response?.data?.conflicts;
    throw wrapped;
  }
};
